import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Newspaper, 
  Users, 
  LogOut, 
  Menu, 
  X,
  Home,
  KeyRound,
  BookOpen
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [username, setUsername] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsername(response.data.username);
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_username');
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/portfolio', icon: Building2, label: 'Property Showcase' },
    { path: '/admin/units', icon: KeyRound, label: 'Available Listings' },
    { path: '/admin/leads', icon: Users, label: 'Leads' },
    { path: '/admin/news', icon: Newspaper, label: 'News' },
    { path: '/admin/blogs', icon: BookOpen, label: 'Blogs' },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-gray-900 to-black text-white transition-all duration-300 flex flex-col shadow-2xl`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h1 className="text-xl font-bold">GN Management</h1>
                <p className="text-xs text-gray-400 mt-1">Admin Panel</p>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#20B2AA] text-white shadow-lg'
                    : 'hover:bg-gray-800 text-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-700">
          <div className={`${sidebarOpen ? 'p-4' : 'p-2'} bg-gray-800 rounded-xl mb-2`}>
            {sidebarOpen ? (
              <div>
                <p className="text-sm font-medium">{username}</p>
                <p className="text-xs text-gray-400">Administrator</p>
              </div>
            ) : (
              <div className="w-8 h-8 bg-[#20B2AA] rounded-full flex items-center justify-center text-sm font-bold">
                {username.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Website Link */}
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-800 rounded-xl transition-colors text-gray-300 mb-2"
          >
            <Home className="h-5 w-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">View Website</span>}
          </Link>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-600 rounded-xl transition-colors text-gray-300 hover:text-white"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
